'use client';

import { Fragment, useCallback, useRef, type MouseEvent } from 'react';
import { useTranslations } from 'next-intl';
import CustomLink from 'components/shared/CustomLink';
import {
  RING_PRO_US_STATS_DISCLAIMER_ID,
  scrollToRingProUsStatsDisclaimer,
} from 'utils/ringProUsBatteryDisclaimer';
import { useCountUpOnView } from './useCountUpOnView';
import styles from './RingProUsStats.module.css';

const STATS = ['battery', 'heartRate', 'sleep', 'ovulation'] as const;

type StatKey = (typeof STATS)[number];

/** Accuracy detail pages for the three agreement stats (ledger slugs). */
const STAT_ACCURACY_HREF: Partial<
  Record<StatKey, `/science/accuracy/${string}`>
> = {
  heartRate: '/science/accuracy/dayhr-rest',
  sleep: '/science/accuracy/sleep-duration',
  ovulation: '/science/accuracy/cycle-ovulation',
};

/** Footnotes are links only — never show marker chars from catalog copy. */
const stripInlineFootnotes = (value: string) =>
  value.replace(/[*†∗§·+]/g, '').trimEnd();

/** "15 days" -> ["15", " days"]; "96.7%" -> ["96.7", "%"]. */
const splitLeadingNumber = (value: string) => {
  const cleaned = stripInlineFootnotes(value);
  const match = /^(\d[\d,.]*)(.*)$/.exec(cleaned);
  return match ? { num: match[1], rest: match[2] } : null;
};

type StatFootnoteProps = {
  statKey: StatKey;
  batteryLabel: string;
  accuracyLabel?: string;
  accuracyHref?: `/science/accuracy/${string}`;
};

const StatFootnote = ({
  statKey,
  batteryLabel,
  accuracyLabel,
  accuracyHref,
}: StatFootnoteProps) => {
  const onBatteryClick = useCallback(
    (event: MouseEvent<HTMLAnchorElement>) => {
      event.preventDefault();
      scrollToRingProUsStatsDisclaimer();
    },
    [],
  );

  if (statKey === 'battery') {
    return (
      <CustomLink
        href={`#${RING_PRO_US_STATS_DISCLAIMER_ID}`}
        className={styles.footnote}
        aria-label={batteryLabel}
        onClick={onBatteryClick}
      >
        *
      </CustomLink>
    );
  }

  if (!accuracyHref || !accuracyLabel) return null;

  return (
    <CustomLink
      href={accuracyHref}
      className={`${styles.footnote} ${styles.footnoteScience}`}
      aria-label={accuracyLabel}
      data-buttontype={`ring_pro_us_stats_accuracy_${statKey}`}
    >
      +
    </CustomLink>
  );
};

/**
 * The number counts up in place. A hidden copy of the final number holds the
 * width, so the unit beside it never shifts while the digits change.
 */
const StatValue = ({
  value,
  statKey,
  batteryFootnoteAria,
  accuracyFootnoteAria,
  accuracyHref,
}: {
  value: string;
  statKey: StatKey;
  batteryFootnoteAria: string;
  accuracyFootnoteAria?: string;
  accuracyHref?: `/science/accuracy/${string}`;
}) => {
  const parts = splitLeadingNumber(value);
  if (!parts) {
    return (
      <span className={styles.value}>{stripInlineFootnotes(value)}</span>
    );
  }

  return (
    <span className={styles.value}>
      <span className={styles.num}>
        <span className={styles.numGhost} aria-hidden>
          {parts.num}
        </span>
        <span data-count-to={parts.num.replace(/,/g, '')}>{parts.num}</span>
      </span>
      {parts.rest}
      <StatFootnote
        statKey={statKey}
        batteryLabel={batteryFootnoteAria}
        accuracyLabel={accuracyFootnoteAria}
        accuracyHref={accuracyHref}
      />
    </span>
  );
};

/**
 * Proof strip under the US hero: four hard product facts between hairlines.
 * Figma: UH Landing Page / Homepage 2517:10102.
 */
export const RingProUsStats = () => {
  const t = useTranslations('homepage');
  const sectionRef = useRef<HTMLElement>(null);
  useCountUpOnView(sectionRef);

  const batteryFootnoteAria = t('homepage.ringProUs.stats.footnote.battery');

  return (
    <section
      ref={sectionRef}
      className={styles.section}
      aria-label={t('homepage.ringProUs.stats.aria.section')}
    >
      <ul className={styles.row}>
        {STATS.map((key, i) => (
          <Fragment key={key}>
            {/* Divider is its own flex item so space-between centres it in
                each gap, whatever the stat widths. */}
            {i > 0 ? (
              <li className={styles.divider} aria-hidden role="presentation" />
            ) : null}
            <li className={styles.stat}>
              <StatValue
                statKey={key}
                value={t(`homepage.ringProUs.stats.${key}.value`)}
                batteryFootnoteAria={batteryFootnoteAria}
                accuracyFootnoteAria={
                  key === 'battery'
                    ? undefined
                    : t(`homepage.ringProUs.stats.footnote.${key}`)
                }
                accuracyHref={STAT_ACCURACY_HREF[key]}
              />
              <span className={styles.label}>
                {t(`homepage.ringProUs.stats.${key}.label`)}
              </span>
            </li>
          </Fragment>
        ))}
      </ul>
    </section>
  );
};

export default RingProUsStats;
